import { useState, useMemo, useCallback } from 'react';
import { useGT } from 'gt-react';
import {
  MultiSelect,
  type MultiSelectOption,
} from '@/components/common/MultiSelect';
import useCustomers from '@/api/accounts-receivable/customers/useCustomers';
import { useDebounce } from '@/hooks/useDebounce';

interface CustomerMultiSelectProps {
  value: MultiSelectOption[];
  onChange: (selected: MultiSelectOption[]) => void;
  size?: 'sm' | 'default' | 'small';
  className?: string;
}

export function CustomerMultiSelect({
  value,
  onChange,
  size = 'sm',
  className = 'h-8 min-w-[180px]',
}: CustomerMultiSelectProps) {
  const gt = useGT();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const { data } = useCustomers({
    search: debouncedSearch || undefined,
    pageSize: 50,
  });

  const options = useMemo(
    (): MultiSelectOption[] =>
      (data?.edges ?? []).map((c) => ({
        id: c.id,
        label: c.erpDisplayName ?? c.name ?? c.id,
      })),
    [data],
  );

  const handleChange = useCallback(
    (opts: MultiSelectOption[]) =>
      onChange(opts.map((o) => ({ id: o.id, label: o.label }))),
    [onChange],
  );

  const handleSearchChange = useCallback((s: string) => setSearch(s), []);

  return (
    <MultiSelect
      options={options}
      value={value}
      onChange={handleChange}
      onSearchChange={handleSearchChange}
      placeholder={gt('All customers', {
        context: 'placeholder when no customers are selected',
      })}
      searchPlaceholder={gt('Search customers...', {
        context: 'search input placeholder in customer multi-select',
      })}
      size={size}
      shouldAutoSort={true}
      className={className}
    />
  );
}
